import { getUserData } from '@src/auth/utils'

const PERMISSION_ALIASES = {
  content_library: 'content',
  quick_screens: 'quick_screen'
}

export const NAV_PERMISSION_MAP = {
  dashboard: 'dashboard',
  screens: 'screens',
  'screens-list': 'screens',
  'quick-add': 'quick_screen',
  'content-library': 'content',
  playlist: 'playlists',
  'group-screens': 'groups',
  reports: 'reports',
  health: 'health_monitor'
}

export const PERMISSION_DEFAULT_ROUTES = {
  dashboard: '/dashboard',
  screens: '/screens',
  quick_screen: '/quick-add',
  content: '/content-library',
  playlists: '/playlist',
  groups: '/groups',
  reports: '/reports',
  health_monitor: '/health-monitor'
}

const PERMISSION_ROUTE_ORDER = [
  'dashboard',
  'screens',
  'quick_screen',
  'content',
  'playlists',
  'groups',
  'reports',
  'health_monitor'
]

const ROUTE_PERMISSION_RULES = [
  { pattern: /^\/dashboard(?:\/|$)/, permission: 'dashboard' },
  { pattern: /^\/quick-add(?:\/|$)/, permission: 'quick_screen' },
  { pattern: /^\/screens(?:\/|$)/, permission: 'screens' },
  { pattern: /^\/content-library(?:\/|$)/, permission: 'content' },
  { pattern: /^\/contents(?:\/|$)/, permission: 'content' },
  { pattern: /^\/playlist(?:\/|$)/, permission: 'playlists' },
  { pattern: /^\/groups(?:\/|$)/, permission: 'groups' },
  { pattern: /^\/reports(?:\/|$)/, permission: 'reports' },
  { pattern: /^\/health-monitor(?:\/|$)/, permission: 'health_monitor' }
]

const ALWAYS_ALLOWED_ROUTE_PATTERNS = [
  /^\/profile(?:\/|$)/,
  /^\/configurations(?:\/|$)/,
  /^\/misc\/not-authorized(?:\/|$)/
]

export const normalizePermissionKey = (permission) => {
  if (!permission) return ''
  return PERMISSION_ALIASES[permission] || permission
}

const applyPageAccess = (map, rawKey, sourceValue) => {
  const formKey = normalizePermissionKey(rawKey)
  if (!formKey) return

  if (Array.isArray(sourceValue)) {
    map[formKey] = {
      read: sourceValue.includes('read'),
      write: sourceValue.includes('write')
    }
    return
  }

  if (typeof sourceValue === 'boolean') {
    map[formKey] = { read: sourceValue, write: sourceValue }
    return
  }

  if (typeof sourceValue === 'object' && sourceValue !== null) {
    map[formKey] = {
      read: Boolean(sourceValue.read ?? sourceValue.can_read ?? sourceValue.access),
      write: Boolean(sourceValue.write ?? sourceValue.can_write ?? sourceValue.access)
    }
  }
}

export const normalizePermissions = (permissions) => {
  const map = {}

  if (!permissions) return map

  if (typeof permissions === 'object' && !Array.isArray(permissions)) {
    Object.keys(permissions).forEach((key) => {
      applyPageAccess(map, key, permissions[key])
    })
    return map
  }

  if (Array.isArray(permissions)) {
    permissions.forEach((item) => {
      if (typeof item === 'string') {
        applyPageAccess(map, item, ['read', 'write'])
        return
      }

      const rawKey = item.page_key || item.page || item.module || item.key
      const access = []
      if (item.read ?? item.can_read) access.push('read')
      if (item.write ?? item.can_write) access.push('write')
      if (!access.length && (item.access ?? item.enabled)) {
        access.push('read', 'write')
      }

      applyPageAccess(map, rawKey, access.length ? access : Boolean(item.access))
    })
  }

  return map
}

export const normalizePermissionCodes = (codes) => {
  if (!Array.isArray(codes)) return []
  return [...new Set(codes.map((code) => normalizePermissionKey(code)).filter(Boolean))]
}

export const extractSubUserAuthPermissions = (response = {}) => {
  const permissions = normalizePermissions(response.permissions)
  const permissionCodes = normalizePermissionCodes(response.permission_codes)

  if (permissionCodes.length) {
    return { permissions, permission_codes: permissionCodes }
  }

  const derivedCodes = Object.keys(permissions).filter((key) => permissions[key]?.read || permissions[key]?.write)
  return { permissions, permission_codes: derivedCodes }
}

export const PERMISSION_MODULES = {
  DASHBOARD: 'dashboard',
  SCREENS: 'screens',
  QUICK_SCREEN: 'quick_screen',
  CONTENT: 'content',
  PLAYLISTS: 'playlists',
  GROUPS: 'groups',
  REPORTS: 'reports',
  HEALTH_MONITOR: 'health_monitor'
}

export const PERMISSION_DENIED_MESSAGE = 'You do not have permission to perform this action.'

export const isSubUser = () => {
  const user = getUserData()
  return user?.role === 'sub_user'
}

export const getSubUserPermissionMap = () => {
  if (!isSubUser()) return {}
  return normalizePermissions(getUserData()?.permissions)
}

export const getSubUserPermissionCodes = () => {
  if (!isSubUser()) return []

  const user = getUserData()
  const codes = normalizePermissionCodes(user?.permission_codes)
  if (codes.length) return codes

  const permissionMap = getSubUserPermissionMap()
  return Object.keys(permissionMap).filter((key) => permissionMap[key]?.read || permissionMap[key]?.write)
}

export const hasSubUserPageAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key) return false

  const permissionCodes = getSubUserPermissionCodes()
  if (!permissionCodes.includes(key)) return false

  const access = getSubUserPermissionMap()[key]
  return Boolean(access?.read || access?.write)
}

export const hasSubUserReadAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key || !getSubUserPermissionCodes().includes(key)) return false

  return Boolean(getSubUserPermissionMap()[key]?.read)
}

export const hasSubUserWriteAccess = (pageKey) => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(pageKey)
  if (!key || !getSubUserPermissionCodes().includes(key)) return false

  return Boolean(getSubUserPermissionMap()[key]?.write)
}

export const getSubUserPermissions = () => (
  getSubUserPermissionCodes().filter((pageKey) => hasSubUserPageAccess(pageKey))
)

export const hasPermission = (module, action = 'read') => {
  if (!isSubUser()) return true

  const key = normalizePermissionKey(module)
  if (action === 'write') return hasSubUserWriteAccess(key)
  return hasSubUserPageAccess(key)
}

export const isSubUserWriteRouteAllowed = (pathname = '') => {
  if (!isSubUser()) return true

  const path = pathname.split('?')[0]
  const writeRoutes = [
    { pattern: /^\/screens\/create(?:\/|$)/, module: PERMISSION_MODULES.SCREENS },
    { pattern: /^\/screens\/edit\/[^/]+(?:\/|$)/, module: PERMISSION_MODULES.SCREENS },
    { pattern: /^\/quick-add\/edit\/[^/]+(?:\/|$)/, module: PERMISSION_MODULES.QUICK_SCREEN },
    { pattern: /^\/playlist\/create(?:\/|$)/, module: PERMISSION_MODULES.PLAYLISTS }
  ]

  const matchedRoute = writeRoutes.find((route) => route.pattern.test(path))
  if (!matchedRoute) return true

  return hasPermission(matchedRoute.module, 'write')
}

export const getSubUserWriteRouteFallback = (pathname = '') => {
  const path = pathname.split('?')[0]

  if (/^\/quick-add(?:\/|$)/.test(path)) return PERMISSION_DEFAULT_ROUTES.quick_screen
  if (/^\/screens(?:\/|$)/.test(path)) return PERMISSION_DEFAULT_ROUTES.screens
  if (/^\/playlist(?:\/|$)/.test(path)) return PERMISSION_DEFAULT_ROUTES.playlists

  const accessiblePages = getSubUserPermissions()
  const matchedKey = PERMISSION_ROUTE_ORDER.find((key) => accessiblePages.includes(key))
  return matchedKey ? PERMISSION_DEFAULT_ROUTES[matchedKey] : '/dashboard'
}

export const buildAbilitiesForUser = (user) => {
  if (!user) {
    return [{ action: 'manage', subject: 'login' }]
  }

  if (user.isAdvertiser === true) {
    return [{ action: 'manage', subject: 'all' }]
  }

  if (user.role === 'sub_user') {
    const permissionMap = normalizePermissions(user.permissions)
    const permissionCodes = normalizePermissionCodes(user.permission_codes)
    const pageKeys = (permissionCodes.length ? permissionCodes : Object.keys(permissionMap))
      .filter((key) => permissionMap[key]?.read || permissionMap[key]?.write)

    const abilities = [
      ...pageKeys.map((permission) => ({ action: 'manage', subject: permission })),
      ...pageKeys.filter((permission) => permissionMap[permission]?.read).map((permission) => ({ action: 'read', subject: permission })),
      ...pageKeys.filter((permission) => permissionMap[permission]?.write).map((permission) => ({ action: 'write', subject: permission })),
      { action: 'manage', subject: 'settings' }
    ]

    // Hide profile / change-password while publisher is impersonating
    if (!user.is_impersonating) {
      abilities.push({ action: 'manage', subject: 'profile' })
    }

    return abilities
  }

  return [
    { action: 'manage', subject: 'publisher' },
    { action: 'manage', subject: 'profile' },
    { action: 'manage', subject: 'settings' },
    { action: 'manage', subject: 'login' }
  ]
}

export const getSubUserDefaultRoute = () => {
  const accessiblePages = getSubUserPermissions()
  const matchedKey = PERMISSION_ROUTE_ORDER.find((key) => accessiblePages.includes(key))
  return matchedKey ? PERMISSION_DEFAULT_ROUTES[matchedKey] : '/misc/not-authorized'
}

export const getDefaultRouteForLoggedInUser = () => {
  if (isSubUser()) return getSubUserDefaultRoute()
  return '/screens'
}

export const canAccessQuickAdd = () => (
  isSubUser() && hasSubUserPageAccess(PERMISSION_MODULES.QUICK_SCREEN)
)

export const canWriteQuickAdd = () => (
  isSubUser() && hasPermission(PERMISSION_MODULES.QUICK_SCREEN, 'write')
)

export const canAccessPublisherScreenDrafts = () => !isSubUser()

export const canAccessPublisherActivityLogs = () => !isSubUser()

export const filterNavigationForUser = (items = []) => {
  if (!isSubUser()) {
    return items
      .filter((item) => item.id !== 'quick-add')
      .map((item) => {
        if (!item.children) return item
        return {
          ...item,
          children: item.children.filter((child) => child.id !== 'quick-add')
        }
      })
  }

  return items.reduce((acc, item) => {
    if (item.id === 'quick-add') {
      if (canAccessQuickAdd()) acc.push(item)
      return acc
    }

    // Publisher-only menus — never for sub-users
    if (item.id === 'sub-users' || item.id === 'activity-logs') {
      return acc
    }

    // Sub-users get a flat Screens link only — never Lists/Drafts submenu
    if (item.id === 'screens') {
      if (hasSubUserPageAccess(PERMISSION_MODULES.SCREENS)) {
        acc.push({
          id: 'screens',
          title: 'Screens',
          icon: item.icon,
          action: item.action,
          resource: item.resource,
          navLink: '/screens'
        })
      }
      return acc
    }

    if (item.children) {
      const visibleChildren = item.children.filter((child) => {
        if (child.id === 'screens-drafts' || child.id === 'quick-add') return false
        const requiredPermission = NAV_PERMISSION_MAP[child.id]
        if (requiredPermission === undefined) return false
        return hasSubUserPageAccess(requiredPermission)
      })

      if (!visibleChildren.length) return acc

      if (visibleChildren.length === 1 && visibleChildren[0].navLink) {
        acc.push({
          ...item,
          children: undefined,
          navLink: visibleChildren[0].navLink
        })
        return acc
      }

      acc.push({ ...item, children: visibleChildren })
      return acc
    }

    const requiredPermission = NAV_PERMISSION_MAP[item.id]
    if (requiredPermission === undefined) return acc
    if (hasSubUserPageAccess(requiredPermission)) acc.push(item)
    return acc
  }, [])
}

export const getRoutePermission = (pathname = '') => {
  const path = pathname.split('?')[0]

  if (ALWAYS_ALLOWED_ROUTE_PATTERNS.some((pattern) => pattern.test(path))) {
    return null
  }

  const matchedRule = ROUTE_PERMISSION_RULES.find((rule) => rule.pattern.test(path))
  return matchedRule ? matchedRule.permission : undefined
}

export const isSubUserRouteAllowed = (pathname = '') => {
  if (!isSubUser()) return true

  const path = pathname.split('?')[0]
  if (/^\/screens\/drafts(?:\/|$)/.test(path)) return false
  if (/^\/sub-users(?:\/|$)/.test(path)) return false
  if (/^\/activity-logs(?:\/|$)/.test(path)) return false

  const requiredPermission = getRoutePermission(pathname)

  if (requiredPermission === null) return true
  if (requiredPermission === undefined) return false

  return hasSubUserPageAccess(requiredPermission)
}
